import { Text } from '@dixa/lib-shared-habla';
import type { PhoneNumber } from 'lib-shared-legacy-types';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { editOrganization } from 'components/settings/actions/organization';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import type { WithPhoneNumbersInjectedProps } from 'hocs';

import PrettyNumber from '../PrettyNumber';

type PropsT = {
  isShown: boolean;
  number: PhoneNumber | null;
  onHide: () => void;
} & WithPhoneNumbersInjectedProps;

const OutboundNumberModal: FC<PropsT> = ({
  isShown,
  number,
  phoneNumbers,
  onHide,
}) => {
  const { getItems } = phoneNumbers;
  const {
    meta: { asyncStatus },
    organization: { id: orgId, default_outbound_number: outboundNumber },
  } = useSelector((state: RootReducerStateT) => state.settings.organization);
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const updateOutboundNumber = useCallback(() => {
    if (!number) {
      return;
    }

    const { e164_format: default_outbound_number } = number;

    setIsLoading(true);

    dispatch(
      editOrganization(
        orgId,
        {
          updates: {
            default_outbound_number,
          },
        },
        {
          title: 'Success',
          text: `${default_outbound_number} is now the default caller ID for your organization`,
          className: 'success',
          type: 'organizationSaved',
          icon: 'i-done',
          time: 5000,
        },
      ),
    );
  }, [orgId, number, dispatch, setIsLoading]);

  /*
   * Check when in redux state the selected outbound number becomes selected.
   */
  useEffect(() => {
    if (!number) {
      return;
    }

    const { e164_format } = number;
    const isOutboundNumberSelected = outboundNumber === e164_format;

    if (isLoading && isOutboundNumberSelected) {
      setIsLoading(false);
      getItems();
      onHide();
    }
  }, [
    asyncStatus,
    number,
    outboundNumber,
    isLoading,
    setIsLoading,
    getItems,
    onHide,
  ]);

  if (!number) {
    return null;
  }

  return (
    <ConfirmModal
      isShown={isShown}
      headline="Set as default caller ID"
      confirmText="Save"
      cancelText="Cancel"
      confirmVariant="primary"
      confirmProps={{ isLoading }}
      confirm={updateOutboundNumber}
      cancel={onHide}
    >
      <>
        <Text.Paragraph withMarginBottom>
          You’re about to set the following phone number as the default caller
          ID for your <strong>entire organization:</strong>
        </Text.Paragraph>
        <Text.Paragraph variant="1" withMarginBottom>
          <PrettyNumber number={number} />
        </Text.Paragraph>
        <Text.Paragraph>
          This will be the phone number your customers see every time calls are
          made from your organization.
        </Text.Paragraph>
      </>
    </ConfirmModal>
  );
};

export default OutboundNumberModal;
